import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import api from 'state/axios';
import { format, compareAsc } from "date-fns";
import toast from 'react-hot-toast';
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { LAYOUT_HEIGHT } from 'state/constants';
import { filterCrewMember } from 'state/helpers';
import {
    countStackedFlightsBy2Fields,
    countRosterDetailsByField,
    countRosterDetails,
    countItemsByTableau,
} from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import CommonTableau from './CommonTableau';
import PieChart from 'components/chartNivo/PieChart';
import SortBarChart from 'components/chartNivo/SortBarChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "rosterByDate", label: "by Date" },
        { value: "rosterByDep", label: "by Port" },
        { value: "rosterByPostBase", label: "by Rank & Base" },
        { value: "rosterByRank", label: "by Rank" },
        { value: "rosterByBase", label: "by Base" },
    ],
    tableau: [
        { value: "byDate", label: "by Date" },
        { value: "byDateDep", label: "by Date & Port" },
        { value: "byDateCategory", label: "by Date & Category" },
        { value: "byDateRank", label: "by Date & Rank" },
        { value: "byDateBase", label: "by Date & Base" },
        { value: "byDep", label: "by Port" },
        { value: "byCategory", label: "by Category" },
        { value: "byRank", label: "by Rank" },
        { value: "byBase", label: "by Base" },
    ],
};

const RosterDetailsChart = () => {    
    useAuthCheck();
    const timeMode = useSelector((state) => state.timemode);    
    const crewRanksData = useSelector((state) => state.crewRanks);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const { startDate, endDate } = useDateRangeContext();

    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    
    const [displayType, setDisplayType] = useState('chart');
    const [scheduleCodes, setScheduleCodes] = useState([]);
    const [view, setView] = useState('rosterByDate');

    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view) ? view : options[0]?.value || '';
    }, [view, displayType]);
    const [rosterDetails, setRosterDetails] = useState([]);


    const [data, setData] = useState({
        rostersByFlightDateCategory: [],
        groupedRosterByDate: [],
        rosterByDate: [],
        
        rostersByDepCategory: [],
        pieRosterByDep: [],
        rosterByDep: [],
        
        rosterByPostBase: [],

        rostersByRankCategory: [],
        pieRosterByRank: [],
        rosterByRank: [],   

        rostersByBaseCategory: [],
        pieRosterByBase: [],
        rosterByBase: [],
    });
          
    useEffect(() => {
        const fetchRosterDetails = async () => {
            if (!startDate || !endDate) return [];

            try {
                setLoading(true);        
                const url = '/rosterdetails';
                const payload = { startDate, endDate };
                
                const res = await api.post(url, payload);
                
                if (res.data) {        
                    const filteredData = res.data?.filter((roster) => {            
                        const flightDateToCompare =
                            timeMode === 'UTC' ? roster.flightDate : roster.flightDateLocal;
                    
                        return (
                            compareAsc(flightDateToCompare, startDate) >= 0 &&
                            compareAsc(flightDateToCompare, endDate) <= 0
                        );
                    });
                    
                    setRosterDetails(filteredData);  
                }
            } catch (error) {
                setError(error);
                toast.error(error?.message || "Failed to fetch roster details!");
            } finally {
                setLoading(false);
            }
        };
      
        fetchRosterDetails();
    }, [timeMode, startDate, endDate]); 
   
    const filteredRosterDetails = useMemo(() => {
        if (!Array.isArray(rosterDetails)) return [];

        let result = rosterDetails;

        if (filters) {
            result = result.filter(member => filterCrewMember(member, filters));
        }

        if (scheduleCodes?.length > 0) {
            result = result.filter(roster => scheduleCodes.includes(roster.flightNo));
        }

        return result;
    }, [rosterDetails, filters, scheduleCodes]);  

    useEffect(() => {
        const flightDate = timeMode === 'UTC' 
            ? 'flightDate' 
            : 'flightDateLocal';  
        
        const results = {
            rostersByFlightDateCategory: countStackedFlightsBy2Fields(filteredRosterDetails, flightDate, 'category', 'count'),
            groupedRosterByDate: countRosterDetailsByField(filteredRosterDetails, flightDate, 'groupedColumn'),
            rosterByDate: countRosterDetailsByField(filteredRosterDetails, flightDate, 'column'),
            
            rostersByDepCategory: countStackedFlightsBy2Fields(filteredRosterDetails, 'dep', 'category', 'count'),
            pieRosterByDep: countRosterDetailsByField(filteredRosterDetails, 'dep', 'pie'),
            rosterByDep: countRosterDetailsByField(filteredRosterDetails, 'dep', 'column'),
            
            rosterByPostBase: countRosterDetails(filteredRosterDetails, 'postbase'),
            
            rostersByRankCategory: countStackedFlightsBy2Fields(filteredRosterDetails, 'post', 'category', 'count'),
            pieRosterByRank: countRosterDetailsByField(filteredRosterDetails, 'post', 'pie'),
            rosterByRank: countRosterDetails(filteredRosterDetails, 'post'),         
            
            rostersByBaseCategory: countStackedFlightsBy2Fields(filteredRosterDetails, 'base', 'category', 'count'),
            pieRosterByBase: countRosterDetailsByField(filteredRosterDetails, 'base', 'pie'),
            rosterByBase: countRosterDetails(filteredRosterDetails, 'base'),
        };
        
        setData(results); 
    }, [timeMode, filteredRosterDetails]); 
    
    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const flightDate = timeMode === 'UTC' ? 'flightDate' : 'flightDateLocal';
        const fieldMap = {
            byDate:         [flightDate, null],
            byDateDep:      [flightDate, 'dep'],
            byDateCategory: [flightDate, 'category'],
            byDateRank:     [flightDate, 'post'],
            byDateBase:     [flightDate, 'base'],
            byDep:          ['dep', null],
            byCategory:     ['category', null],
            byRank:         ['post', null],
            byBase:         ['base', null],
        };
        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countItemsByTableau(filteredRosterDetails, fields[0], fields[1]);
    }, [displayType, safeView, timeMode, filteredRosterDetails]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const scheduleCodeOptions = useMemo(() => {
        if (!Array.isArray(rosterDetails)) return [];
    
        return Array.from(
            new Map(rosterDetails
                .filter(r => !r.routeNo && r.flightNo)
                .map(r => [r.flightNo, r])
            ).values()
        )
        .sort((a, b) => {
            const catA = a.category ?? "";
            const catB = b.category ?? "";

            if (catA !== catB) {
            return catA.localeCompare(catB);
            }

            return (a.flightNo ?? "").localeCompare(b.flightNo ?? "");
        })
        .map(item => {
            return { scheduleCode: item.flightNo, description: item.description }
        });
    }, [rosterDetails]);

    const {
        baseOptions,
        postOptions,
        trainingQualOptions,
    } = useMemo(() => {
        if (!Array.isArray(rosterDetails) || rosterDetails.length === 0) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
            };
        }

        const baseSet = new Set();
        const postSet = new Set();
        const trainingQualSet = new Set();

        rosterDetails.forEach(r => {
            if (r.base) baseSet.add(r.base);
            if (r.post) postSet.add(r.post);

            r.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
        });

        const baseOptions = [...baseSet].sort();
        const postOptions = [...postSet]
            .map(post => crewRanksData.find(r => r.rankCode === post))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(r => r.rankCode);
        const trainingQualOptions = [...trainingQualSet].sort();
        
        return {
            baseOptions,
            postOptions,
            trainingQualOptions,
        };
    }, [rosterDetails, crewRanksData]);  

    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const period = `${startDate ? format(startDate, 'ddMMMyy') : ''} - ${endDate ? format(endDate, 'ddMMMyy') : ''}`;
    const defaultTitle = `Roster details ${period}`;

    const chartMap = {
        rosterByDate: [
            { component: <SortStackedBarChart data={data.rostersByFlightDateCategory} title={defaultTitle} bottomLegend='Date' leftLegend='Count' digit={0} translateXOffSet={25} itemsSpacingOffSet={-30} />, style: {} },
            { component: <SortBarChart data={data.groupedRosterByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Count" isShowAvgLine={false} isSortDisabled={true} translateXOffSet={5} itemWidthOffSet={-15} />, style: {} },
            { component: <SortBarChart data={data.rosterByDate} title={defaultTitle} bottomLegend="Date" leftLegend="Count" digit={0} />, style: {} },
        ],
        rosterByDep: [
            { component: <SortStackedBarChart data={data.rostersByDepCategory} title={defaultTitle} bottomLegend='Port' leftLegend='Count' digit={0} translateXOffSet={25} itemsSpacingOffSet={-30} />, style: {} },
            { component: <PieChart data={data.pieRosterByDep} title={defaultTitle} itemWidthOffSet={isMobile ? -50 : -50}  />, style: {} },
            { component: <SortBarChart data={data.rosterByDep} title={defaultTitle} bottomLegend="Port" leftLegend="Count" digit={0} />, style: {} },
        ],
        rosterByPostBase: [
            { component: <SortBarMultiLinesChart data={data.rosterByPostBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={100} lineTranslateXOffSet={-10} />, style: {} },
        ],
        rosterByRank: [
            { component: <SortStackedBarChart data={data.rostersByRankCategory} title={defaultTitle} bottomLegend='Rank' leftLegend='Count' digit={0} translateXOffSet={25} itemsSpacingOffSet={-30} />, style: {} },
            { component: <PieChart data={data.pieRosterByRank} title={defaultTitle} itemWidthOffSet={isMobile ? -50 : -50}  />, style: {} },
            { component: <SortBarMultiLinesChart data={data.rosterByRank} title={defaultTitle} bottomLegend='Rank' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={100} lineTranslateXOffSet={-10} />, style: {} },
        ],        
        rosterByBase: [
            { component: <SortStackedBarChart data={data.rostersByBaseCategory} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} translateXOffSet={25} itemsSpacingOffSet={-30} />, style: {} },
            { component: <PieChart data={data.pieRosterByBase} title={defaultTitle} itemWidthOffSet={isMobile ? -50 : -50}  />, style: {} },
            { component: <SortBarMultiLinesChart data={data.rosterByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={100} lineTranslateXOffSet={-10} />, style: {} },
        ],
    }; 
    
    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Roster Details"
                    subtitle={loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '12rem' } }}
                        >
                            <InputLabel id="schedule-codes-label">Schedule Codes</InputLabel>
                            <Select
                                multiple
                                label="Schedule Codes"
                                value={scheduleCodes}
                                onChange={(e) => setScheduleCodes(e.target.value)}
                            >
                                {scheduleCodeOptions?.map((item, index) => (
                                    <MenuItem key={index} value={item.scheduleCode}>
                                        {item.scheduleCode} {item.description ? ` - ${item.description}` : ""}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        {!isMobile && (
                            <FormControl 
                                size="small" 
                                sx={{ width: { xs: '6rem', sm: '8rem' } }}
                            >
                                <InputLabel>Display</InputLabel>
                                <Select
                                    value={displayType}
                                    label="Display"
                                    onChange={(e) => setDisplayType(e.target.value)}
                                >
                                    {displayOptions.map(opt => (
                                        <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '9rem' } }}
                        >
                            <InputLabel id="roster-view-label">View</InputLabel>
                            <Select
                                labelId="roster-view-label"
                                label="View"
                                value={safeView}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                    sx={{ "& > div": { gridColumn: isDesktop ? undefined : "span 12" } }}
                >
                    {displayType === 'chart' && safeView in chartMap &&
                        renderCharts(chartMap[safeView], { minHeight: LAYOUT_HEIGHT })
                    }
                    {displayType === 'tableau' &&
                        renderCharts([{ component: <CommonTableau data={tableauData} mode="count" />, style: {} }], { minHeight: LAYOUT_HEIGHT })
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
 
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}  

            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    trainingQualOptions={trainingQualOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default RosterDetailsChart;
